import { IHbciAccount } from '../../Interfaces/IHbciAccount';
import { IProcessManager } from '../../Interfaces/IProcessManager';

/**
 * Zustandslogik der Notaranderkonto-Verfolgungen (ProcessManager Typ 22) —
 * bewusst als reine Funktionen, damit die Regeln testbar sind und die Zeile
 * selbst nur noch rendert.
 */

/** Verknüpfung zwischen Verfolgung und Notaranderkonto. */
export type HbciTrackerAccountState =
  /** Konto ist geladen und verknüpft. */
  | 'ok'
  /** Konten werden gerade geladen — noch keine Aussage möglich. */
  | 'loading'
  /** Konten konnten nicht geladen werden (z. B. Erweiterung inaktiv → 403).
   *  Wir behaupten dann NICHT, das Konto sei weg. */
  | 'unknown'
  /** Die Verfolgung trägt keine (gültige) ``idHbciAccount``. */
  | 'unlinked'
  /** Verknüpftes Konto existiert nicht mehr (Konten geladen, ID nicht dabei). */
  | 'missing';

/** Chip-Farben, die die Zeile verwendet — bewusst ohne MUI-Import. */
export type HbciTrackerStatusColor = 'default' | 'info' | 'success';

export interface HbciTrackerStatus {
  label: string;
  color: HbciTrackerStatusColor;
  variant: 'filled' | 'outlined';
}

/**
 * Liest die verknüpfte Konto-ID aus ``data.idHbciAccount``. Bestandsdaten
 * enthalten die ID teils als String, deshalb wird normalisiert. Alles, was
 * keine positive Zahl ergibt, gilt als "nicht verknüpft".
 */
export const readTrackerAccountId = (pm: IProcessManager): number | null => {
  const raw = (pm.data as { idHbciAccount?: number | string } | undefined)
    ?.idHbciAccount;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * Ermittelt den Verknüpfungs-Zustand einer Verfolgung. ``missing`` ist der
 * Fall, der früher unsichtbar blieb: die Zeile zeigte die rohe ``#<id>`` und
 * einen "Aktiv"-Chip, obwohl das Konto längst gelöscht war.
 */
export const getTrackerAccountState = ({
  idHbciAccount,
  account,
  isLoadingAccounts = false,
  hasAccountLoadError = false,
}: {
  idHbciAccount: number | null;
  account?: IHbciAccount;
  isLoadingAccounts?: boolean;
  hasAccountLoadError?: boolean;
}): HbciTrackerAccountState => {
  if (idHbciAccount === null) return 'unlinked';
  if (account) return 'ok';
  if (isLoadingAccounts) return 'loading';
  // Fehlgeschlagener Abruf ist keine Aussage über die Existenz des Kontos.
  if (hasAccountLoadError) return 'unknown';
  return 'missing';
};

/**
 * Status-Chip der Verfolgung. Wichtig: der PM-State entscheidet mit — eine
 * abgeschlossene Verfolgung (State != 1, z. B. vom Sweep bei Zielerreichung
 * geschlossen) darf nicht als "Aktiv" erscheinen.
 *
 * ``targetLocked`` = das Backend hat ``data.TargetReachedAt`` eingefroren.
 * Das ist der belastbare Nachweis der Zielerreichung, auch wenn aktuell kein
 * Saldo vorliegt (Konto weg, Abruf fehlgeschlagen).
 */
export const getTrackerStatus = ({
  isNew,
  idProcessManagerState,
  targetReached,
  targetLocked,
}: {
  isNew: boolean;
  idProcessManagerState: number;
  targetReached: boolean;
  targetLocked: boolean;
}): HbciTrackerStatus => {
  if (isNew) {
    return {
      label: 'Noch nicht gespeichert',
      color: 'info',
      variant: 'outlined',
    };
  }
  if (targetReached || targetLocked) {
    return { label: 'Zielsumme erreicht', color: 'success', variant: 'filled' };
  }
  if (idProcessManagerState !== 1) {
    return { label: 'Abgeschlossen', color: 'default', variant: 'filled' };
  }
  return { label: 'Aktiv', color: 'default', variant: 'outlined' };
};

/**
 * Alle anderen Verfolgungen, die dasselbe Konto benutzen — über **alle**
 * States. Der Check entscheidet, ob das Konto beim Entfernen der Verfolgung
 * mit gelöscht werden darf; eine abgeschlossene Verfolgung (State 3) zeigt
 * genauso auf das Konto wie eine offene und würde sonst verwaisen.
 */
export const getTrackersUsingAccount = (
  processManagerArray: IProcessManager[],
  pm: IProcessManager,
  idHbciAccount: number | null
): IProcessManager[] => {
  if (idHbciAccount === null) return [];
  return processManagerArray.filter(
    candidate =>
      candidate.idProcessManager !== pm.idProcessManager &&
      candidate.idProcessManagerType === 22 &&
      readTrackerAccountId(candidate) === idHbciAccount
  );
};
